import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiClock } from 'react-icons/fi';
import { useTheme } from '../../contexts/ThemeContext';

interface ScheduledEvent {
  id: string;
  title: string;
  event_type: string;
  next_run_at?: string;
  scheduled_at?: string;
  is_recurring?: boolean;
}

interface NextFiringStripProps {
  events: ScheduledEvent[];
}

function formatCountdown(ms: number): string {
  if (ms <= 0) return 'now';
  const mins = Math.floor(ms / 60000);
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  const remMins = mins % 60;
  return remMins > 0 ? `${hrs}h ${remMins}m` : `${hrs}h`;
}

const TYPE_COLORS: Record<string, string> = {
  reminder: 'text-amber-400',
  check_status: 'text-sky-400',
  pulse: 'text-indigo-400',
  custom: 'text-gray-400',
};

const NextFiringStrip: React.FC<NextFiringStripProps> = ({ events }) => {
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const [now, setNow] = useState(Date.now());

  // Tick every 60 seconds for countdown
  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(interval);
  }, []);

  // Get next 2 upcoming events
  const upcoming = events
    .map(e => ({ ...e, fireAt: new Date(e.next_run_at || e.scheduled_at || '').getTime() }))
    .filter(e => e.fireAt > now)
    .sort((a, b) => a.fireAt - b.fireAt)
    .slice(0, 2);

  if (upcoming.length === 0) return null;

  return (
    <div className={`flex items-center gap-4 px-3 py-2 rounded-lg border-l-4 border-amber-500 text-xs ${
      darkMode ? 'bg-gray-800/60' : 'bg-white'
    }`}>
      <FiClock className="w-3.5 h-3.5 text-amber-400 shrink-0" />
      {upcoming.map(e => {
        const isPulse = e.event_type === 'pulse';
        return (
          <span
            key={e.id}
            className={`flex items-center gap-1.5 ${TYPE_COLORS[e.event_type] || 'text-gray-400'} ${isPulse ? 'cursor-pointer' : ''}`}
            onClick={isPulse ? () => navigate('/heartbeat') : undefined}
          >
            {isPulse ? (
              <span
                className="w-2 h-2 rounded-full bg-indigo-400 shrink-0"
                style={{ animation: 'breathe 2s ease-in-out infinite' }}
              />
            ) : (
              <span>&#9670;</span>
            )}
            <span className={darkMode ? 'text-gray-300' : 'text-gray-600'}>{e.title}</span>
            <span className="font-mono font-medium">fires in {formatCountdown(e.fireAt - now)}</span>
            {e.is_recurring && <span className="text-gray-500">&#8635;</span>}
          </span>
        );
      })}
    </div>
  );
};

export default NextFiringStrip;
